/**
 * UX Sprint 1 — More Tools drawer (slide-over from the right).
 *
 * Houses the advanced/internal tabs that USED to clutter the top tab bar:
 *   - Activity Log (was Agent Tasks)
 *   - Strategy Approval raw (was Strategy Lock)
 *   - Advanced Shot Planning (was Sequence Planning)
 *   - Video Prompts Library (was Prompt Pack Library)
 *   - Quality Check (was QC Review)
 *   - Delivery Tools (was Post-Production)
 *   - Share Link (also accessible from the header)
 *
 * Clicking an entry closes the drawer and routes via `onPickTab(tabKey)`.
 * Every existing tab key is preserved — deep links keep working.
 */
import { useEffect } from "react";
import {
    X, ListChecks, ShieldCheck, Workflow, BookOpen,
    ClipboardCheck, PackageCheck, Link2,
} from "lucide-react";

const TOOLS = [
    {
        key: "tasks", label: "Activity Log", icon: ListChecks,
        desc: "Per-agent run history, retries, and outputs.",
    },
    {
        key: "strategy", label: "Strategy Approval (raw)", icon: ShieldCheck,
        desc: "Inspect strategy lock state and approval gating.",
    },
    {
        key: "video_workflow", label: "Advanced Shot Planning", icon: Workflow,
        desc: "Power-user sequence planning surface.",
    },
    {
        key: "prompts", label: "Video Prompts Library", icon: BookOpen,
        desc: "Prompt-pack library and CSV/PDF exports.",
    },
    {
        key: "qc", label: "Quality Check", icon: ClipboardCheck,
        desc: "QC review notes and approval gates.",
    },
    {
        key: "post", label: "Delivery Tools", icon: PackageCheck,
        desc: "Post-production handoff instructions.",
    },
    {
        key: "share", label: "Share / Review Link", icon: Link2,
        desc: "Public client review URL controls.",
    },
];

export default function MoreToolsDrawer({ open, onClose, onPickTab }) {
    useEffect(() => {
        if (!open) return;
        const onKey = (e) => { if (e.key === "Escape") onClose && onClose(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, onClose]);

    return (
        <>
            {/* Backdrop */}
            <div
                data-testid="more-tools-backdrop"
                onClick={onClose}
                className={`fixed inset-0 z-40 bg-black/60 backdrop-blur-sm transition-opacity ${
                    open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
                }`}
                aria-hidden={!open}
            />
            {/* Panel */}
            <aside
                data-testid="more-tools-drawer"
                aria-hidden={!open}
                className={`fixed top-0 right-0 z-50 h-screen w-full sm:w-[420px] border-l border-zinc-900 bg-[#080808] shadow-2xl flex flex-col transition-transform duration-200 ${
                    open ? "translate-x-0" : "translate-x-full"
                }`}
            >
                <header className="px-5 py-4 border-b border-zinc-900 flex items-center justify-between">
                    <div>
                        <div className="label-micro text-[#FF4400]">Advanced</div>
                        <h2 className="font-display text-lg tracking-tight">More Tools</h2>
                    </div>
                    <button
                        type="button"
                        data-testid="more-tools-close"
                        onClick={onClose}
                        className="w-9 h-9 grid place-items-center rounded-sm border border-zinc-900 hover:border-zinc-700 hover:bg-zinc-900/50 text-zinc-400 hover:text-white transition-colors"
                        aria-label="Close"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </header>
                <div className="flex-1 overflow-y-auto scrollbar-thin px-3 py-3 space-y-1">
                    {TOOLS.map(({ key, label, icon: Icon, desc }) => (
                        <button
                            key={key}
                            type="button"
                            data-testid={`more-tools-${key}`}
                            onClick={() => { onPickTab && onPickTab(key); onClose && onClose(); }}
                            className="w-full flex items-start gap-3 px-3 py-3 rounded-sm border border-transparent hover:border-zinc-800 hover:bg-zinc-900/40 text-left transition-colors group"
                        >
                            <div className="shrink-0 w-9 h-9 rounded-sm border border-zinc-800 grid place-items-center text-zinc-400 group-hover:text-[#FF4400] group-hover:border-[#FF4400]/40 transition-colors">
                                <Icon className="w-4 h-4" />
                            </div>
                            <div className="min-w-0">
                                <div className="text-sm text-white">{label}</div>
                                <div className="text-[11px] text-zinc-500 mt-0.5">{desc}</div>
                            </div>
                        </button>
                    ))}
                </div>
                <footer className="px-5 py-3 border-t border-zinc-900 text-[10px] font-mono uppercase tracking-wider text-zinc-600">
                    Advanced surfaces — all power-user tools live here.
                </footer>
            </aside>
        </>
    );
}
